import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from '@m/alchemy-ui/Button';
import { DateRangePicker } from '@m/alchemy-ui/DateRangePicker';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import { LineChart } from '@m/alchemy-ui/LineChart';
import { useIntl } from 'react-intl';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import { GET_ACTIVITY, GET_COUNTRIES, type GetActivityQuery, type GetCountriesQuery } from '../../graphql/queries';
import { defaultTypes, StatType, StatTypeOptions, StatTypeScoreTitle } from '../../common/enums';
import { useUserFields, useWindowWidth } from '../../hooks';
import { CommunityContext } from '../../context';
import { type StatResultType } from '../../common/core-api';
import { CustomFilters } from './CustomFilters';
import { type UserFieldType } from './types';
import { ChartExportMenu, CheckboxFilter, Score, TableView } from './components';
import {
  ChartActionsRow,
  ChartScroll,
  Grid,
  GridCol,
  GridHeader,
  GridRow,
  ScoreCardItem,
  ScoreCardsRow
} from './Analytics.styled';
import * as Styled from './components/SearchFilters.styled';
import { accessibleChartTheme, accessibleSeriesLineColors, getChartScrollWidth } from './helpers/chartUtils';

const formatDate = (date: Date) => date.toISOString().slice(0, 10);

export const ActivityOverview = ({ dataTableViewEnabled }: { dataTableViewEnabled: boolean }) => {
  const intl = useIntl();
  const seriesAmount = 6;
  const community = useContext(CommunityContext);
  const { data: communityUserFields } = useUserFields();

  let initialEndDate = new Date(Date.now());
  initialEndDate = new Date(formatDate(initialEndDate).replace(/-/g, '/'));
  const [endDate, setEndDate] = useState(initialEndDate);
  const initialStartDate = new Date(Date.now());
  initialStartDate.setDate(initialStartDate.getDate() - 28);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [selectedStartDate, setSelectedStartDate] = useState(startDate);
  const [selectedEndDate, setSelectedEndDate] = useState(endDate);
  const [isUpdateDisabled, setIsUpdateDisabled] = useState(false);

  const defaultUserFieldOptions: UserFieldType = {};
  const [userFieldOptions, setUserFieldOptions] = useState(defaultUserFieldOptions);

  const [countries, setCountries] = useState<string[]>([]);
  const [skip, setSkip] = useState<boolean>(false);
  const [types, setTypes] = useState<number[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<number[]>([]);
  const [statsData, setStatsData] = useState<GetActivityQuery>({ stats: [] });
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data: countriesList } = useQuery<GetCountriesQuery>(GET_COUNTRIES, {
    skip,
    variables: { id: community.id }
  });

  const windowWidth = useWindowWidth();
  const chartScrollWidth = useMemo(() => getChartScrollWidth(windowWidth), [windowWidth]);

  useEffect(() => {
    if (countriesList?.community) {
      countriesList.community && setCountries(countriesList.community.countries as string[]);
    }
  }, [countriesList]);

  const variables = {
    type: types.length === 0 ? defaultTypes : types,
    startDate,
    endDate,
    filters: { userFields: JSON.stringify(userFieldOptions) }
  };

  const { loading, data } = useQuery<GetActivityQuery>(GET_ACTIVITY, {
    skip,
    variables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    if (!skip && data?.stats) {
      setSelectedTypes(types);
      setStatsData(data);
      setSelectedStartDate(startDate);
      setSelectedEndDate(endDate);
    }
  }, [skip, data, types, startDate, endDate]);

  const getScoreTitle = useCallback(
    (title: string): string => {
      switch (title) {
        case StatTypeScoreTitle.VOTE:
          return intl.formatMessage({ id: 'vote', defaultMessage: 'Vote' });
        case StatTypeScoreTitle.IDEA_CREATED:
          return intl.formatMessage({ id: 'ideaCreated', defaultMessage: 'Idea' });
        case StatTypeScoreTitle.COMMENT_CREATED:
          return intl.formatMessage({ id: 'commentCreated', defaultMessage: 'Comment' });
        case StatTypeScoreTitle.POST_VIEW:
          return intl.formatMessage({ id: 'postView', defaultMessage: 'Post View' });
        case StatTypeScoreTitle.PAGE_VIEW:
          return intl.formatMessage({ id: 'pageView', defaultMessage: 'Page view' });
        case StatTypeScoreTitle.USER_JOIN:
          return intl.formatMessage({ id: 'userJoin', defaultMessage: 'User join' });
        default:
          return '';
      }
    },
    [intl]
  );

  const formatScreenReaderXValue = useCallback(
    (value: number | string): string => {
      if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
          return intl.formatDate(parsed, { year: 'numeric', month: 'long', day: 'numeric' });
        }
      }

      return String(value);
    },
    [intl]
  );

  const formatScreenReaderValue = useCallback(
    (value: number | string, point: { [key: string]: unknown }): string => {
      const numericValue = typeof value === 'number' ? value : Number(value);
      const formattedValue = Number.isFinite(numericValue) ? intl.formatNumber(numericValue) : String(value);
      const parsedPoint = point as { x?: string; seriesLabel?: string; label?: string };
      const seriesLabel = parsedPoint.seriesLabel || parsedPoint.label;
      const xValue = parsedPoint.x ? formatScreenReaderXValue(parsedPoint.x) : undefined;

      return `${xValue} ${seriesLabel}: ${formattedValue}`;
    },
    [formatScreenReaderXValue, intl]
  );

  const { chartData, finalStats } = useMemo(() => {
    const finalStats = Array.from({ length: seriesAmount }).fill(0) as number[];

    const dates: { [index: string]: number[] } = {};
    const dt = new Date(selectedStartDate.toString());

    dates[formatDate(dt)] = Array.from({ length: seriesAmount }).fill(0) as number[];

    while (formatDate(dt) !== formatDate(selectedEndDate)) {
      dt.setDate(dt.getDate() + 1);
      dates[formatDate(dt)] = Array.from({ length: seriesAmount }).fill(0) as number[];
    }

    if (statsData?.stats) {
      statsData.stats.forEach((result: StatResultType) => {
        if (result?.date && result.date in dates && result?.type) {
          dates[result.date][result.type - 1] += result.total;
          finalStats[result.type - 1] += result.total;
        }
      });
    }

    const typesToDisplay = selectedTypes.length === 0 ? defaultTypes : selectedTypes;
    const chartData = typesToDisplay.map((typeId) => {
      const dataPoints = [];
      const currentDate = new Date(selectedStartDate.toString());

      dataPoints.push({
        x: formatDate(currentDate),
        y: dates[formatDate(currentDate)]?.[typeId - 1] || 0
      });

      while (formatDate(currentDate) !== formatDate(selectedEndDate)) {
        currentDate.setDate(currentDate.getDate() + 1);
        dataPoints.push({
          x: formatDate(currentDate),
          y: dates[formatDate(currentDate)]?.[typeId - 1] || 0
        });
      }

      return {
        id: typeId.toString(),
        label: getScoreTitle(StatType[typeId]),
        color: accessibleSeriesLineColors[typeId - 1],
        data: dataPoints
      };
    });

    return { chartData, finalStats };
  }, [selectedStartDate, selectedEndDate, selectedTypes, statsData, getScoreTitle, seriesAmount]);

  const tableBaseColumns = useMemo(
    () => [
      { label: intl.formatMessage({ id: 'date', defaultMessage: 'Date' }) },
      { label: intl.formatMessage({ id: 'vote', defaultMessage: 'Vote' }) },
      { label: intl.formatMessage({ id: 'ideaCreated', defaultMessage: 'Idea created' }) },
      { label: intl.formatMessage({ id: 'commentCreated', defaultMessage: 'Comment created' }) },
      { label: intl.formatMessage({ id: 'postView', defaultMessage: 'Post view' }) },
      { label: intl.formatMessage({ id: 'pageView', defaultMessage: 'Page view' }) },
      { label: intl.formatMessage({ id: 'userJoin', defaultMessage: 'User join' }) }
    ],
    [intl]
  );

  const buildTableRowsBase = useCallback(() => {
    const rows: (Date | number)[][] = [];
    const dates: { [index: string]: number[] } = {};
    const dt = new Date(selectedStartDate.toString());

    dates[formatDate(dt)] = Array.from({ length: seriesAmount }).fill(0) as number[];
    while (formatDate(dt) !== formatDate(selectedEndDate)) {
      dt.setDate(dt.getDate() + 1);
      dates[formatDate(dt)] = Array.from({ length: seriesAmount }).fill(0) as number[];
    }

    if (statsData?.stats) {
      statsData.stats.forEach((result: StatResultType) => {
        if (result?.date && result.date in dates && result?.type) {
          dates[result.date][result.type - 1] += result.total;
        }
      });
    }

    for (const key in dates) {
      if (Object.hasOwn(dates, key)) {
        const tempRow = ([new Date(key.replace(/-/g, '/'))] as unknown as number[]).concat(
          statsData?.stats ? dates[key] : (Array.from({ length: seriesAmount }).fill(0) as number[])
        );
        rows.push(tempRow as unknown as (Date | number)[]);
      }
    }

    return rows;
  }, [selectedStartDate, selectedEndDate, statsData, seriesAmount]);

  const buildTableData = useCallback(() => {
    const tableRowsBase = buildTableRowsBase();
    const tableColumns = tableBaseColumns.filter(
      (_, index) => index === 0 || selectedTypes.length === 0 || selectedTypes.includes(index)
    );
    const tableRows = tableRowsBase.map((row) => {
      const newRow = [row[0]];
      selectedTypes.length === 0
        ? newRow.push(...row.slice(1))
        : selectedTypes.forEach((type) => newRow.push(row[type]));
      return newRow;
    });
    return { columns: tableColumns, rows: tableRows };
  }, [buildTableRowsBase, selectedTypes, tableBaseColumns]);

  const tableData = useMemo(() => {
    if (!dataTableViewEnabled) {
      return null;
    }

    return buildTableData();
  }, [buildTableData, dataTableViewEnabled]);

  const tableColumns = tableData?.columns ?? [];
  const tableRows = tableData?.rows ?? [];

  const scoreCardTypes = selectedTypes.length === 0 ? defaultTypes : selectedTypes;
  const scoreCardsCompact = dataTableViewEnabled && scoreCardTypes.length < seriesAmount;

  if (loading && !data) {
    return (
      <Styled.LoadingContainer>
        <LoadingIndicator inline />
      </Styled.LoadingContainer>
    );
  }

  return (
    <Grid key="activityOverview">
      <CustomFilters
        userFieldOptions={userFieldOptions}
        communityUserFields={communityUserFields}
        countries={countries}
        userFieldUpdate={setUserFieldOptions}
        setSkip={setSkip}
      />

      <GridHeader key="dateRangeHeader">
        {intl.formatMessage({ id: 'dateRange', defaultMessage: 'Date range' })}
      </GridHeader>
      <GridRow key="dateRange">
        <DateRangePicker
          start={{
            label: intl.formatMessage({ id: 'start', defaultMessage: 'Start' }),
            date: startDate,
            onDateChange: (date: Date | null) => {
              const newStartDate = date || new Date();
              const endDateOffset = new Date(endDate);
              endDateOffset.setDate(endDateOffset.getDate() + 1);
              setSkip(true);
              setStartDate(newStartDate);
              setIsUpdateDisabled(newStartDate > endDateOffset);
            }
          }}
          end={{
            label: intl.formatMessage({ id: 'end', defaultMessage: 'End' }),
            date: endDate,
            onDateChange: (date: Date | null) => {
              const newEndDate = date || new Date();
              const newEndDateOffset = new Date(newEndDate);
              newEndDateOffset.setDate(newEndDateOffset.getDate() + 1);
              setSkip(true);
              setEndDate(newEndDate);
              setIsUpdateDisabled(startDate > newEndDateOffset);
            }
          }}
        />
      </GridRow>
      <GridHeader key="dataSelectionHeader">{intl.formatMessage({ id: 'show', defaultMessage: 'Show' })}</GridHeader>
      <GridRow key="dataSelection">
        <CheckboxFilter
          id="types"
          name={intl.formatMessage({ id: 'data', defaultMessage: 'Data' })}
          selectedFilters={types.map((type) => StatTypeOptions[StatType[type]])}
          setTypes={setTypes}
          setSkip={setSkip}
          userFieldOptions={userFieldOptions}
          communityUserFields={communityUserFields}
          options={Object.values(StatTypeOptions)}
        />
      </GridRow>
      <GridRow key="updateButton">
        <Button
          priority="secondary"
          onClick={() => {
            setSkip(false);
          }}
          disabled={isUpdateDisabled}
        >
          {intl.formatMessage({ id: 'update', defaultMessage: 'Update' })}
        </Button>
      </GridRow>
      {!dataTableViewEnabled && (
        <ChartActionsRow key="activityOverviewExport">
          <ChartExportMenu
            chartRef={chartContainerRef}
            fileName={intl.formatMessage({ id: 'activityOverview', defaultMessage: 'Activity Overview' })}
            formatDate={formatDate}
            getExportData={buildTableData}
          />
        </ChartActionsRow>
      )}
      <GridRow key="annotationChart">
        {dataTableViewEnabled ? (
          <TableView
            columns={tableColumns}
            rows={tableRows}
            formatDate={formatDate}
            summary={intl.formatMessage({
              id: 'activityOverview',
              defaultMessage: 'Activity Overview'
            })}
          />
        ) : (
          <ChartScroll width={chartScrollWidth} ref={chartContainerRef}>
            <ThemeProvider theme={accessibleChartTheme}>
              <LineChart
                aria-label={intl.formatMessage({
                  id: 'activityOverviewChartAriaLabel',
                  defaultMessage: 'Activity over time line chart'
                })}
                onDataClick={() => {}}
                axisY={{
                  formatScreenReaderValue: (value, point) => formatScreenReaderValue(value, point)
                }}
                data={chartData}
              />
            </ThemeProvider>
          </ChartScroll>
        )}
      </GridRow>
      <ScoreCardsRow key="scoreCards" $compact={scoreCardsCompact} gutter={scoreCardsCompact ? 0 : undefined}>
        {finalStats.map((v, k) => {
          const title = getScoreTitle(StatType[k + 1]);
          if (!scoreCardTypes.includes(k + 1)) {
            return null;
          }

          return scoreCardsCompact ? (
            <ScoreCardItem key={title}>
              <Score type={k} title={title} value={v} dataTableViewEnabled={dataTableViewEnabled} />
            </ScoreCardItem>
          ) : (
            <GridCol span={2} key={title}>
              <Score type={k} title={title} value={v} dataTableViewEnabled={dataTableViewEnabled} />
            </GridCol>
          );
        })}
      </ScoreCardsRow>
    </Grid>
  );
};
